import React, { useState } from 'react';
import axios from 'axios';
import CustomSelect from '../widgets/CustomSelect';
import CodeInput from '../widgets/CodeInput';
import '../../styles/MedicationLog.css';

const MedicationLog = ({ logId, orderId, scheduledTime, scheduledDate, isNewPRN, onComplete, onCancel }) => {
  const [nurse, setNurse] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [timeWarning, setTimeWarning] = useState('');

  // Calculate time variance when component mounts or scheduled time changes
  React.useEffect(() => {
    if (scheduledTime && scheduledDate) {
      const now = new Date();
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
      const timeDifferenceHours = Math.abs(now - scheduledDateTime) / (1000 * 60 * 60);
      
      if (timeDifferenceHours > 2) {
        setTimeWarning('⚠️ Administration time varies from scheduled time by more than 2 hours. Consider adjusting the schedule for remaining doses.');
      } else {
        setTimeWarning('');
      }
    }
  }, [scheduledTime, scheduledDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nurse || !pin) {
      setError('Please select a nurse and enter PIN');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // Verify PIN
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/verify-code`,
        { employee_id: nurse, security_code: pin }
      );

      if (verifyResponse.data.result === 'Approved') {
        let updateResponse;
        
        if (logId) {
          // Update existing medication log
          updateResponse = await axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-log`,
            {
              nurse_id: nurse,
              administration_datetime: new Date().toISOString(),
              log_id: logId,
              order_id: orderId,
              scheduled_time: scheduledTime,
              scheduled_date: scheduledDate,
              is_prn: false
            }
          );
        } else {
          // Create new PRN log
          updateResponse = await axios.post(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-log`,
            {
              order_id: orderId,
              nurse_id: nurse,
              scheduled_time: scheduledTime,
              scheduled_date: scheduledDate,
              administration_datetime: new Date().toISOString(),
              is_prn: true
            }
          );
        }

        onComplete(updateResponse.data);
      } else {
        setError('Invalid PIN');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="medication-log-overlay">
      <div className="medication-log-modal">
        <h3>Log Medication Administration</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nurse</label>
            <CustomSelect
              name="employee_name"
              valueField="employee_id"
              labelField="employee_name"
              value={nurse}
              onChange={(name, value) => setNurse(value)}
              endpoint="/hospital-portal/api/employees/byrole?role=Nurse"
              placeholder="Select nurse..."
              isSearchable={true}
            />
          </div>

          <div className="form-group">
            <label>Enter PIN</label>
            <CodeInput
              length={4}
              value={pin}
              onChange={setPin}
              autoFocus
            />
          </div>

          {timeWarning && <div className="time-warning" style={{color: 'red', margin: '10px 0', fontSize: '14px'}}>{timeWarning}</div>}
          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button 
              type="submit" 
              disabled={verifying || !nurse || pin.length !== 4}
            >
              {verifying ? 'Verifying...' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicationLog;
